<template>
  <v-menu
    v-model="showMenu"
    :close-on-content-click="false"
    :offset="[calendarPopupNudgeRight, 0]"
    transition="scale-transition"
    min-width="auto"
  >
    <template v-slot:activator="{ props }">
      <div class="d-flex datetime-control" dense>
        <v-text-field
          class="calendarpart"
          v-bind="props"
          :bg-color="highlight ? 'metadataHighlight' : 'transparent'"
          :variant="isLineItem ? 'solo-filled' : undefined"
          :class="{ 'pt-1': isLineItem }"
          :hide-details="isLineItem ? 'auto' : ''"
          @blur="onBlur"
          @focus="hasFocus = true"
          ref="textField"
          v-maska="dateMask"
          :disabled="documentMetadata.metadata.isReadonly"
          v-model="formattedDate"
          :label="label"
          :append-icon="highlight && documentMetadata.autoExtracted ? 'mdi-auto-fix' : 'mdi-calendar'"
          :data-cy="documentMetadata.metadata.key"
        ></v-text-field>
        <v-text-field class="centered-input ml-2" v-maska="timeMask" label="hh:mm" v-model="timePart"></v-text-field>
        <v-select :items="amPmValues" v-model="amPmPart" class="centered-select ml-2"></v-select>
      </div>
    </template>
    <v-date-picker v-model="datePart" @update:modelValue="showMenu = false"></v-date-picker>
  </v-menu>
</template>

<script>
import { mapState } from "vuex";
import moment from "moment";
import dateService from "@/services/dateService";
import documentMetadataService from "@/services/documentMetadataService";
import responsiveService from "@/services/responsiveService";
import metadataMixin from "@/mixins/metadataMixin";

export default {
  name: "DateTimeMetadata",

  // Use the mixin to handle prop mutations properly
  mixins: [metadataMixin],

  props: {
    // documentMetadata is provided by the mixin
    textSelection: Object,
    isLineItem: Boolean,
    focusOnLoad: Boolean,
    onClickToFill: Function,
  },
  data() {
    return {
      showMenu: false,
      highlight: false,
      manualDateEntry: null,
      amPmValues: ["AM", "PM"],
      amPmPart: "AM",
      timePart: null,
      datePart: null,
      hasFocus: false,
    };
  },
  watch: {
    textSelection: {
      handler() {
        // Check if this field has focus using our tracked state
        if (!this.hasFocus) return;

        const formattedDate = moment(this.textSelection.text).format("YYYY-MM-DD");
        this.localValue = formattedDate;
        documentMetadataService.assignTextSelection(this);
        this.manualDateEntry = formattedDate; // Use the formatted value directly, not from localValue getter

        // Show highlight effect when text is selected from PDF
        this.highlight = true;
        setTimeout(() => {
          this.highlight = false;
        }, 2000);

        if (this.onClickToFill) this.onClickToFill();
      },
      deep: true,
    },

    amPmPart() {
      this.localValue = this.fullValue;
    },
    timePart() {
      this.localValue = this.fullValue;
    },
    datePart() {
      this.localValue = this.fullValue;
    },
    focusOnLoad() {
      documentMetadataService.focusIfRequested(this);
    },
  },

  created() {
    if (this.documentMetadata.value) {
      let parsedDate = dateService.parseDateTime(this.documentMetadata.value);
      this.datePart = parsedDate.datePart;
      let hours = +parsedDate.timePart.split(":")[0];
      let hours12 = hours > 12 ? hours - 12 : hours;
      this.timePart = `${hours12}:${parsedDate.timePart.split(":")[1]}`;
      this.amPmPart = hours > 12 ? "PM" : "AM";
    }

    // Use the mixin's helper method instead of direct mutation
    this.clearHighlightAfterDelay();
    documentMetadataService.focusIfRequested(this);
  },

  methods: {
    onBlur() {
      this.hasFocus = false;
      this.datePart = this.manualDateEntry;
    },
  },
  computed: {
    ...mapState("userProfile", ["dateFormat"]),

    // localValue is provided by the mixin and emits events on set

    label() {
      return this.isLineItem ? "" : this.documentMetadata.metadata.name;
    },

    dateMask() {
      return dateService.toMask(this.dateFormat);
    },

    timeMask() {
      return "##:##";
    },
    fullValue() {
      if (!this.datePart) return null;
      let time = "00:00";
      if (this.timePart?.length > 4) {
        time = this.timePart;
        if (this.amPmPart === "PM") {
          let hours = +this.timePart.slice(0, 2) + 12;
          let minutes = this.timePart.slice(3, 5);
          time = hours + ":" + minutes;
        }
      }
      return this.datePart + "T" + time + ":00";
    },
    calendarPopupNudgeRight() {
      return responsiveService.isMobile(this.$vuetify.display) ? -70 : 0;
    },

    formattedDate: {
      get: function() {
        if (!this.datePart || !this.dateFormat) return "";
        return moment(this.datePart).format(this.dateFormat.toUpperCase());
      },
      set: function(newValue) {
        let date = moment(newValue, this.dateFormat.toUpperCase(), true);
        if (date.isValid()) {
          this.manualDateEntry = date.format("YYYY-MM-DD");
        } else {
          this.manualDateEntry = null;
        }
      },
    },
  },
};
</script>

<style scoped>
.calendarpart {
  width: 200px;
}
.centered-input {
  width: 45px;
}
.datetime-control {
  margin-bottom: -13px;
}
.centered-select {
  width: 58px;
}

.centered-input :deep(input) {
  text-align: center;
}
.centered-input :deep(label) {
  text-align: center;
  width: 100%;
}
</style>
